import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from './hooks/useAuth'
import StitchBorder from './components/ui/StitchBorder'
import Landing from './pages/Landing'
import Dashboard from './pages/Dashboard'
import Workspace from './pages/Workspace'
import Pricing from './pages/Pricing'
import AuthCallback from './pages/AuthCallback'
import Admin from './pages/Admin'
import Privacy from './pages/privacy'
import Terms from './pages/terms'
import MarketplacePage from './components/MarketplacePage'
import CommunityGallery from './components/CommunityGallery'
import GalleryPost from './pages/GalleryPost'
import Orders from './pages/Orders'
import SellerDashboard from './pages/SellerDashboard'
import CommandPalette from './components/CommandPalette'
import KeyboardShortcutsModal from './components/KeyboardShortcutsModal'
import UserProfilePage from './components/UserProfilePage'

// Protected route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zenna-cream dark:bg-slate-900">
        <div className="text-center">
          <div className="w-12 h-12 rounded-xl bg-gradient-zenna flex items-center justify-center mx-auto mb-4 animate-pulse">
            <span className="text-white font-bold text-xl">Z</span>
          </div>
          <p className="text-slate-500 dark:text-slate-400">Loading...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />
  }

  return children
}

// Wrapper to pass auth into marketplace/community pages
const AuthAwarePage = ({ Component, protectedRoute = false }) => {
  const { user, isAuthenticated } = useAuth()
  if (protectedRoute && !isAuthenticated) return <Navigate to="/" replace />
  return <Component userEmail={user?.email || ''} />
}

const ProfilePage = ({ isOwnProfile = false }) => {
  const { user } = useAuth()
  const params = isOwnProfile ? {} : undefined
  return <UserProfilePage isOwnProfile={isOwnProfile} currentUserEmail={user?.email || ''} />
}

function AppInner() {
  const { user } = useAuth()
  return (
    <>
      <CommandPalette userEmail={user?.email || ''} />
      <KeyboardShortcutsModal />
      {/* Animated stitch border - always visible */}
      <StitchBorder />
      
      {/* Main content */}
      <div className="relative z-10">
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<Landing />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/marketplace" element={<AuthAwarePage Component={MarketplacePage} />} />
          <Route path="/gallery" element={<AuthAwarePage Component={CommunityGallery} />} />
          <Route path="/gallery/:postId" element={<GalleryPost />} />
          
          {/* Protected routes */}
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/workspace" 
            element={
              <ProtectedRoute>
                <Workspace />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin" 
            element={
              <ProtectedRoute>
                <Admin />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/orders"
            element={
              <ProtectedRoute>
                <Orders />
              </ProtectedRoute>
            }
          />
          <Route
            path="/seller"
            element={
              <ProtectedRoute>
                <SellerDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <ProfilePage isOwnProfile />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/profile/:userId" 
            element={<ProfilePage />} 
          />
          
          {/* Catch all - redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </>
  )
}

function App() {
  return <AppInner />
}

export default App